import {BrowserRouter, Outlet, Route, Routes, useNavigate} from "react-router-dom";
import {Suspense, useEffect, lazy} from "react";
import {jwtDecode} from "jwt-decode";
import {AuthClient} from "../store/clients/auth";
import {TokenPair} from "../models/user";
import { CircularProgress, Box } from "@mui/material";
import AccountPage from "../view/pages/account_page";
import {
    getAccessToken,
    setAccessToken,
    setRefreshToken,
    clearAllTokens
} from "../store/credential_manager";

// Lazy load all page components
const HomePage = lazy(() => import("../view/pages/home_page"));
const AdminPage = lazy(() => import("../view/pages/admin_page"));
const BlogPage = lazy(() => import("../view/pages/blog_page"));
const ContactPage = lazy(() => import("../view/pages/contact_page"));
const RegisterPage = lazy(() => import("../view/pages/register_page"));
const AboutPage = lazy(() => import("../view/pages/about_page"));
const LoginPage = lazy(() => import("../view/pages/login_page"));
const PricingPage = lazy(() => import("../view/pages/pricing_page"));
const DocumentationPage = lazy(() => import("../view/pages/documentation_page"));
const PageNotFoundPage = lazy(() => import("../view/pages/page_not_found_page"));
const UnsubscribeNewsletterPage = lazy(() => import("../view/pages/unsubscribe_newsletter_page"));
const NewsletterPage = lazy(() => import("../view/pages/newsletter_page"));
const TestimonialPage = lazy(() => import("../view/pages/testimonial_page"));
const PaymentSuccessPage = lazy(() => import("../view/pages/payment_success_page"));
const PaymentCancelPage = lazy(() => import("../view/pages/payment_cancel_page"));
const PasswordResetPage = lazy(() => import("../view/pages/password_reset"));

// Loading component for lazy loading
const LoadingSpinner = () => (
    <Box 
        sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100vh',
            width: '100%'
        }}
    >
        <CircularProgress size={60} />
    </Box>
);

export function CustomRouter(){
    return(
        <BrowserRouter>
            <Suspense fallback={<LoadingSpinner />}>
                <Routes>
                    <Route element={<ProtectedLoginRoute />}>
                        <Route path="/login" element={<LoginPage />} />
                        {/*<Route path="/register" element={<RegisterPage />} />*/}
                    </Route>

                    <Route element={<ProtectedAuthRoute />}>
                        <Route path="/account" element={<AccountPage />} />
                        <Route path="/admin" element={<AdminPage />} />
                        <Route path="/payment/success" element={<PaymentSuccessPage />} />
                        <Route path="/payment/cancel" element={<PaymentCancelPage />} />
                    </Route>

                    {/*todo uncomment*/}
                    <Route element={<ProtectedAdminRoute />}>
                        {/*<Route path="/admin" element={<AdminPage />} />*/}
                        <Route path="/blog" element={<BlogPage />} />
                    </Route>

                    <Route>
                        <Route path="/" element={<HomePage />} />
                        {/*<Route path="/welcome" element={<WelcomePage />} />*/}
                        <Route path="/home" element={<HomePage />} />
                        <Route path="/contact" element={<ContactPage />} />
                        <Route path="/about" element={<AboutPage />} />
                        <Route path="/pricing" element={<PricingPage />} />
                        <Route path="/documentation" element={<DocumentationPage />} />
                        <Route path="/newsletter/unsubscribe" element={<UnsubscribeNewsletterPage />} />
                        <Route path="/newsletters" element={<NewsletterPage />} />
                        <Route path="/testimonials" element={<TestimonialPage />} />
                        <Route path="/reset-password" element={<PasswordResetPage />} />
                        <Route path="*" element={<PageNotFoundPage />} />
                    </Route>
                </Routes>
            </Suspense>

        </BrowserRouter>
    )
}

// const ProtectedRoute = () => {
//     const accessTokenExpString = localStorage.getItem('accessTokenExp');
//     const loginExpired = accessTokenExpString != null ? new Date(accessTokenExpString) < new Date() : true;
//     const navigate = useNavigate();
//
//     useEffect(() => {
//         if (loginExpired) {
//             navigate('/login')
//         }
//     }, [loginExpired, navigate]);
//
//     return <Outlet/>
// }

const ProtectedLoginRoute = () => {
    const accessToken = getAccessToken();
    const navigate = useNavigate();

    useEffect(() => {
        const checkAuth = async () => {
            const validToken = await validateAccessToken();
            if (validToken) {
                navigate('/home');
            }
        };

        checkAuth().then();
    }, [accessToken, navigate]);

    return <Outlet/>
}

const ProtectedAuthRoute = () => {
    const accessToken = getAccessToken();
    const navigate = useNavigate();

    useEffect(() => {
        const checkAuth = async () => {
            const validToken = await validateAccessToken();
            if (!validToken) {
                navigate('/login');
            }
        };

        checkAuth().then();
    }, [accessToken, navigate]);

    return <Outlet/>
}

const ProtectedAdminRoute = () => {
    const accessToken = getAccessToken();
    const navigate = useNavigate();

    useEffect(() => {
        const checkAuth = async () => {
            const validToken = await validateAccessToken();
            if (!validToken) {
                navigate('/login'); // if not logged in, go to login page
            } else {
                navigate('/account'); // if not staff user, go to account page
            }
        };

        checkAuth().then();
    }, [accessToken, navigate]);

    return <Outlet/>
}

const validateAccessToken = async () => {
    const accessToken = getAccessToken();

    if (accessToken == null) {
        return false;
    }
    const decoded = jwtDecode(accessToken!);
    const tokenExp = decoded.exp;
    const now = Date.now() / 1000;

    if (tokenExp! < now) {
        return await refreshToken();
    }
    return true;
}

const refreshToken = async () => {
    try {
        const response = await AuthClient.RefreshAccessToken();

        if (response.status === 200) {
            const data = response.data as TokenPair;
            setAccessToken(data.access!);
            setRefreshToken(data.refresh!);
            return true;
        }else {
            clearAllTokens();
            return false;
        }
    }catch (e) {}

    return false;
}
